import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { ApiError } from '../api.js';
import { telHref } from '../format.js';
import { navigate } from '../router.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { Avatar, Callout, Dialog, ErrorBox, Loading, Pill, toast } from '../ui.js';
import { RushesBanners, ShootNotes, type ShootNote } from './rushes.js';
import { AddBall, PersonPicker } from '../personPicker.js';

/**
 * The project's Shoots tab (owner request 2026-10-16; Del 1 UX-980–982 in a first version): the shoots booked in the
 * Hub, Monday's shoot day until the Hub takes it over, and *Book shoot*. Booking sends the calendar invite from
 * coordinator@ to the crew, the coordinator and the client in KFF's own format – or only registers a shoot that was
 * booked by hand. Before booking, each crew member's conflicts are shown (absence, another shoot, busy in Google).
 */

type Role = 'producer' | 'cd' | 'camera' | 'editor' | 'other';
interface ShootView {
  id: string;
  date: string;
  start: string;
  end: string;
  location: string;
  plan: string;
  contact: { id: string; name: string; email: string; phone: string | null } | null;
  guests: { id: string; name: string; email: string }[];
  crew: { id: string; name: string; role: Role; phone: string | null }[];
  coordinator: { id: string; name: string; phone: string | null } | null;
  videos: { id: string; name: string }[];
  status: 'booked' | 'cancelled';
  cancelReason: string | null;
  calendar: { mode: 'hub' | 'manual'; status: 'none' | 'sent' | 'failed' | 'cancelled'; error: string | null; sentAt: string | null; organizer: string | null; invited: string[]; notInvited: string[] };
  past: boolean;
  /** the footage after the shoot (null = no reminder yet) */
  rushes?: { state: 'waiting' | 'up' | 'received'; by: string | null; folder: string | null; files: number | null; bytes: number | null; at: string | null; notes?: ShootNote[]; comments?: string[] } | null;
  /** who acts that day and whether their model release is signed */
  actors?: { name: string; role: string | null; released: boolean }[];
  /** "Inför inspelningen": the camera crew and when each read in (null = not yet) */
  prep?: { id: string; readAt: string | null }[];
  /** a Monday project whose shoot day in Monday is another day: that day (change it in Monday) */
  mondaySays?: string | null;
}
interface Options {
  people: { id: string; name: string; phone: string | null; freelancer: boolean; camera: boolean; onProject: boolean }[];
  contacts: { id: string; name: string; email: string; phone: string | null }[];
  videos: { id: string; name: string }[];
  /** a number for people and contacts with none on file: from an earlier shoot or an old invite */
  suggestions: Record<string, { phone: string; from: 'shoot' | 'invite'; date: string }>;
  defaults: { crew: { personId: string; role: Role }[]; coordinatorId: string | null; contactId: string | null; videoIds: string[] };
}
interface Tab {
  shoots: ShootView[];
  cancelled: ShootView[];
  monday: { date: string } | null;
  productionType: string;
  can: { edit: boolean };
  calendar: { configured: boolean; organizer: string | null; limitedTo: string[] | null };
  options?: Options;
}
type Conflict = { kind: 'free' } | { kind: 'away'; part: 'full' | 'am' | 'pm' } | { kind: 'shoot'; project: string; from: string; to: string } | { kind: 'busy'; from: string; to: string } | { kind: 'unknown' };

const T = texts(
  {
    title: 'Shoots',
    info: 'Book the shoot here: the invite goes out from the coordinator calendar with the crew and the client on it, in the usual format.',
    book: 'Book shoot',
    none: 'No shoot booked yet.',
    noneOther: 'This project has no shoot booked in the Hub.',
    monday: (d: string) => `From Monday · shoot day ${d}`,
    mondaySays: (d: string) => `Monday says ${d} – change the shoot day in Monday`,
    mondaySub: 'The calendar is not managed by the Hub.',
    takeOver: 'Book in the Hub…',
    contact: 'Contact',
    videos: (n: number) => `${n} ${n === 1 ? 'video' : 'videos'}`,
    sent: (n: number) => `Invite sent · ${n} ${n === 1 ? 'guest' : 'guests'}`,
    manual: 'Calendar managed by hand',
    rushesWaiting: 'Rushes missing',
    rushesUp: (by: string | null, n: number | null) => `Rushes up${by ? ` · ${by}` : ''}${n ? ` · ${n} files` : ''}`,
    rushesReceived: 'Rushes received',
    prep: 'Before the shoot',
    prepRead: (n: string) => `${n} has read in ✓`,
    prepUnread: (n: string) => `${n} has not read in yet`,
    actorsOn: 'Actors',
    released: 'release signed',
    notReleased: 'release not signed',
    failed: 'Invite not sent',
    sendInvite: 'Send invite…',
    tryAgain: 'Try again',
    change: 'Change…',
    cancel: 'Cancel shoot…',
    cancelled: 'Cancelled',
    cancelledShoots: (n: number) => `Cancelled shoots (${n})`,
    notInvited: (list: string) => `Not invited here (test environment): ${list}`,
    notSetUp: 'Calendar invites are not set up in the Hub yet. Shoots are saved, and the invite can be sent later.',
    // dialog
    dlgBook: 'Book shoot',
    dlgChange: 'Change shoot',
    date: 'Date',
    start: 'Start',
    end: 'End',
    location: 'Location',
    locationHint: 'Address, as it should read in the invite',
    onSet: 'Contact on set',
    onSetHint: 'Name and phone go into the invite. They are invited too.',
    noContact: 'No contact',
    phone: 'Phone',
    alsoInvite: 'Also invite',
    crew: 'Crew',
    role: { producer: 'Producer', cd: 'CD', camera: 'Camera', editor: 'Editing', other: 'Other' } as Record<Role, string>,
    roleLabel: 'Role on the shoot:',
    fromTeam: "The project's team",
    noPhone: 'No phone',
    missingTitle: 'Phone missing.',
    newPhones: 'New numbers.',
    missingSub: 'It is saved on the profile or contact card, so you only add it once.',
    useSuggestion: (ph: string) => `Use ${ph}`,
    suggestionFrom: (from: 'shoot' | 'invite', date: string) => (from === 'shoot' ? `from the shoot ${date}` : `from an invite ${date}`),
    phonePlaceholder: 'Write a number',
    more: 'More options',
    wholeProject: 'Whole project',
    wholeProjectN: (n: number) => `Whole project (${n} ${n === 1 ? 'video' : 'videos'})`,
    someVideos: 'Only some videos…',
    coordinatorShort: (n: string) => `Coordinator ${n}`,
    guestsShort: (n: number) => `${n} more invited`,
    planShort: 'Plan written',
    noPlan: 'No plan',
    sendFrom: (org: string | null) => (org ? `Send invite from ${org.split('@')[0]}@` : 'Send calendar invite'),
    saveSend: 'Save and send invite',
    addPerson: 'Add person…',
    remove: 'Remove',
    coordinator: 'KFF coordinator',
    filmed: 'Videos filmed',
    plan: 'Plan',
    planHint: 'Timings, scenes, what to bring – goes into the invite.',
    how: 'Calendar',
    howHub: 'Send the calendar invite',
    howHubSub: (org: string | null) => (org ? `From ${org} to the crew, the coordinator and the client.` : 'To the crew, the coordinator and the client.'),
    howManual: 'Only register it',
    howManualSub: 'Already booked in the calendar by hand: nothing is sent.',
    conflictsTitle: 'Conflicts',
    free: 'Free',
    away: { full: 'Away all day', am: 'Away in the morning', pm: 'Away in the afternoon' },
    otherShoot: (p: string, f: string, t: string) => `Shoot ${f}–${t} · ${p}`,
    busy: (f: string, t: string) => `Busy ${f}–${t} in Google`,
    unknown: 'Calendar not available',
    checking: 'Checking calendars…',
    weekend: 'Not a workday',
    conflictWarn: 'Someone has a conflict. Change the crew or time, or book anyway.',
    save: 'Save',
    saving: 'Saving…',
    bookSend: 'Book and send invite',
    bookAnyway: 'Book anyway and send invite',
    register: 'Register shoot',
    saveChange: 'Save and update invite',
    close: 'Close',
    cancelTitle: 'Cancel the shoot?',
    cancelBody: 'Everyone on the invite gets a cancellation from Google.',
    cancelManual: 'The calendar booking was made by hand – remove it yourself.',
    reason: 'Why is it cancelled?',
    doCancel: 'Cancel shoot',
    keep: 'Keep it',
    dow: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    mon: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  },
  {
    title: 'Inspelningar',
    info: 'Boka inspelningen här: inbjudan går ut från koordinatorns kalender med crew och kund, i det vanliga formatet.',
    book: 'Boka inspelning',
    none: 'Ingen inspelning bokad än.',
    noneOther: 'Projektet har ingen inspelning bokad i Hubben.',
    monday: (d: string) => `Från Monday · inspelningsdag ${d}`,
    mondaySays: (d: string) => `Monday säger ${d} – ändra inspelningsdagen i Monday`,
    mondaySub: 'Kalendern sköts inte av Hubben.',
    takeOver: 'Boka i Hubben…',
    contact: 'Kontakt',
    videos: (n: number) => `${n} ${n === 1 ? 'video' : 'videor'}`,
    sent: (n: number) => `Inbjudan skickad · ${n} ${n === 1 ? 'gäst' : 'gäster'}`,
    manual: 'Kalendern sköts manuellt',
    rushesWaiting: 'Rushes saknas',
    rushesUp: (by: string | null, n: number | null) => `Rushes uppe${by ? ` · ${by}` : ''}${n ? ` · ${n} filer` : ''}`,
    rushesReceived: 'Rushes mottagna',
    prep: 'Inför inspelningen',
    prepRead: (n: string) => `${n} har läst in sig ✓`,
    prepUnread: (n: string) => `${n} har inte läst in sig än`,
    actorsOn: 'Skådespelare',
    released: 'modellavtal signerat',
    notReleased: 'modellavtal inte signerat',
    failed: 'Inbjudan inte skickad',
    sendInvite: 'Skicka inbjudan…',
    tryAgain: 'Försök igen',
    change: 'Ändra…',
    cancel: 'Ställ in…',
    cancelled: 'Inställd',
    cancelledShoots: (n: number) => `Inställda inspelningar (${n})`,
    notInvited: (list: string) => `Bjuds inte in här (testmiljö): ${list}`,
    notSetUp: 'Kalenderinbjudningar är inte inställda i Hubben än. Inspelningar sparas, och inbjudan kan skickas senare.',
    dlgBook: 'Boka inspelning',
    dlgChange: 'Ändra inspelning',
    date: 'Datum',
    start: 'Start',
    end: 'Slut',
    location: 'Plats',
    locationHint: 'Adress, så som den ska stå i inbjudan',
    onSet: 'Kontakt på plats',
    onSetHint: 'Namn och telefon hamnar i inbjudan. Kontakten bjuds också in.',
    noContact: 'Ingen kontakt',
    phone: 'Telefon',
    alsoInvite: 'Bjud även in',
    crew: 'Crew',
    role: { producer: 'Producent', cd: 'CD', camera: 'Kamera', editor: 'Redigering', other: 'Annan' },
    roleLabel: 'Roll på inspelningen:',
    fromTeam: 'Projektets team',
    noPhone: 'Telefon saknas',
    missingTitle: 'Telefon saknas.',
    newPhones: 'Nya nummer.',
    missingSub: 'Den sparas på profilen eller kontaktkortet, så du lägger bara in den en gång.',
    useSuggestion: (ph: string) => `Använd ${ph}`,
    suggestionFrom: (from: 'shoot' | 'invite', date: string) => (from === 'shoot' ? `från inspelningen ${date}` : `från en inbjudan ${date}`),
    phonePlaceholder: 'Skriv nummer',
    more: 'Fler val',
    wholeProject: 'Hela projektet',
    wholeProjectN: (n: number) => `Hela projektet (${n} ${n === 1 ? 'video' : 'videor'})`,
    someVideos: 'Bara vissa videor…',
    coordinatorShort: (n: string) => `Koordinator ${n}`,
    guestsShort: (n: number) => `${n} till inbjudna`,
    planShort: 'Plan ifylld',
    noPlan: 'Ingen plan',
    sendFrom: (org: string | null) => (org ? `Skicka inbjudan från ${org.split('@')[0]}@` : 'Skicka kalenderinbjudan'),
    saveSend: 'Spara och skicka inbjudan',
    addPerson: 'Lägg till person…',
    remove: 'Ta bort',
    coordinator: 'KFF-koordinator',
    filmed: 'Videor som spelas in',
    plan: 'Plan',
    planHint: 'Tider, scener, vad som ska med – hamnar i inbjudan.',
    how: 'Kalender',
    howHub: 'Skicka kalenderinbjudan',
    howHubSub: (org: string | null) => (org ? `Från ${org} till crew, koordinator och kund.` : 'Till crew, koordinator och kund.'),
    howManual: 'Bara registrera',
    howManualSub: 'Redan bokad i kalendern för hand: inget skickas.',
    conflictsTitle: 'Krockar',
    free: 'Tillgänglig',
    away: { full: 'Ledig hela dagen', am: 'Ledig på förmiddagen', pm: 'Ledig på eftermiddagen' },
    otherShoot: (p: string, f: string, t: string) => `Inspelning ${f}–${t} · ${p}`,
    busy: (f: string, t: string) => `Upptagen ${f}–${t} i Google`,
    unknown: 'Kalendern gick inte att läsa',
    checking: 'Kollar kalendrar…',
    weekend: 'Inte en arbetsdag',
    conflictWarn: 'Någon har en krock. Byt crew eller tid, eller boka ändå.',
    save: 'Spara',
    saving: 'Sparar…',
    bookSend: 'Boka och skicka inbjudan',
    bookAnyway: 'Boka ändå och skicka inbjudan',
    register: 'Registrera inspelning',
    saveChange: 'Spara och uppdatera inbjudan',
    close: 'Stäng',
    cancelTitle: 'Ställa in inspelningen?',
    cancelBody: 'Alla på inbjudan får en avbokning från Google.',
    cancelManual: 'Kalenderbokningen gjordes för hand – ta bort den själv.',
    reason: 'Varför ställs den in?',
    doCancel: 'Ställ in',
    keep: 'Behåll',
    dow: ['sön', 'mån', 'tis', 'ons', 'tor', 'fre', 'lör'],
    mon: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']
  }
);

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const val = (e: Event) => (e.target as HTMLInputElement).value;
const dt = (k: string) => new Date(`${k}T12:00:00Z`);

function DateBox(p: { date: string; lang: Lang }) {
  const t = T[p.lang];
  const d = dt(p.date);
  return (
    <div class="sh-date" aria-hidden="true">
      <span>{t.dow[d.getUTCDay()]}</span>
      <b>{d.getUTCDate()}</b>
      <span>{t.mon[d.getUTCMonth()]}</span>
    </div>
  );
}

export function ProjectShoots(p: { api: Api; id: string }) {
  const lang = useLang();
  const t = T[lang];
  const [v, setV] = useState<Tab | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [edit, setEdit] = useState<{ shoot?: ShootView; date?: string } | null>(null);
  const [cancelling, setCancelling] = useState<ShootView | null>(null);
  const load = () => p.api.get<Tab>(`/api/projects/${p.id}/shoots`).then(setV, setErr);
  useEffect(() => {
    void load();
    if (typeof location !== 'undefined' && new URLSearchParams(location.search).has('book')) setEdit({});
  }, [p.id]);
  if (err) return <ErrorBox error={err} />;
  if (!v) return <Loading />;
  const done = (r: { text: string }) => {
    toast({ text: r.text });
    void load();
  };
  const invite = async (s: ShootView) => {
    try {
      done(await p.api.post<{ text: string }>(`/api/shoots/${s.id}/invite`));
    } catch (e) {
      toast({ text: errText(e) });
    }
  };
  return (
    <section class="blk sh" aria-labelledby="sh-h">
      <div class="blk-head">
        <h2 id="sh-h" class="blk-title">
          {t.title}
        </h2>
        {v.can.edit ? (
          <button type="button" class="btn btn-primary btn-sm" onClick={() => setEdit({})}>
            + {t.book}
          </button>
        ) : null}
      </div>
      {v.can.edit ? <p class="blk-info">{t.info}</p> : null}
      {v.can.edit && !v.calendar.configured ? (
        <Callout tone="info" title={t.notSetUp} />
      ) : null}
      {/* the rushes to upload (camera) or that are up (editing) for this project, the same banner as on My work */}
      <RushesBanners api={p.api} projectId={p.id} />
      <ul class="sh-list">
        {v.shoots.map((s) => (
          <li class="sh-row" key={s.id}>
            <DateBox date={s.date} lang={lang} />
            <div class="sh-main">
              <p class="sh-when">
                {s.start}–{s.end} · {s.location}
              </p>
              {s.mondaySays ? <p class="small warn-text">{t.mondaySays(s.mondaySays)}</p> : null}
              <p class="small muted sh-sub">
                {s.contact ? (
                  <>
                    {t.contact} {s.contact.name}
                    {s.contact.phone ? (
                      <>
                        {' · '}
                        <a href={telHref(s.contact.phone)}>{s.contact.phone}</a>
                      </>
                    ) : null}
                  </>
                ) : null}
                {s.contact && s.videos.length ? ' · ' : null}
                {s.videos.length ? t.videos(s.videos.length) : null}
              </p>
              <div class="cluster sh-tags">
                <span class="sh-crew">
                  {s.crew.map((m) => (
                    <Avatar name={m.name} size="sm" label={`${m.name} · ${t.role[m.role]}`} />
                  ))}
                </span>
                {/* how the invite went is the producer's business; the crew sees the shoot and the rushes */}
                {!v.can.edit ? null : s.calendar.mode === 'manual' ? (
                  <Pill tone="neutral">{t.manual}</Pill>
                ) : s.calendar.status === 'sent' ? (
                  <Pill tone="ok">{t.sent(s.calendar.invited.length)}</Pill>
                ) : s.calendar.status === 'failed' ? (
                  <Pill tone="crit">{t.failed}</Pill>
                ) : null}
                {s.rushes ? (
                  s.rushes.state === 'waiting' ? (
                    <Pill tone="warn">{t.rushesWaiting}</Pill>
                  ) : s.rushes.state === 'up' ? (
                    <Pill tone="ok">{t.rushesUp(s.rushes.by, s.rushes.files)}</Pill>
                  ) : (
                    <Pill tone="ok">{t.rushesReceived}</Pill>
                  )
                ) : null}
              </div>
              {v.can.edit && s.calendar.status === 'failed' && s.calendar.error ? <p class="small sh-err">{s.calendar.error}</p> : null}
              {v.can.edit && s.calendar.notInvited.length && s.calendar.mode === 'hub' ? <p class="small muted">{t.notInvited(s.calendar.notInvited.join(', '))}</p> : null}
              {s.plan ? <p class="small sh-plan">{s.plan}</p> : null}
              {/* "Inför inspelningen" (owner 2026-11-01): the shoot on one page, and who on the camera crew has read in */}
              {!s.past ? (
                <div class="cluster sh-prep">
                  <a class="btn btn-sm" href={`/projects/${p.id}/shoots/${s.id}`} onClick={(e) => (e.preventDefault(), navigate(`/projects/${p.id}/shoots/${s.id}`))}>
                    {t.prep}
                  </a>
                  {(s.prep ?? []).map((r) => {
                    const name = s.crew.find((m) => m.id === r.id)?.name.split(' ')[0] ?? '–';
                    return r.readAt ? (
                      <Pill key={r.id} tone="ok">
                        {t.prepRead(name)}
                      </Pill>
                    ) : (
                      <Pill key={r.id} tone="neutral">
                        {t.prepUnread(name)}
                      </Pill>
                    );
                  })}
                </div>
              ) : null}
              {s.actors?.length ? (
                <p class="small sh-actors">
                  <strong>{t.actorsOn}:</strong>{' '}
                  {s.actors.map((a, i) => (
                    <span key={`${a.name}${i}`}>
                      {i ? ', ' : ''}
                      {a.name}
                      {a.role ? ` (${a.role})` : ''} <span class={a.released ? 'muted' : 'sh-err'}>· {a.released ? t.released : t.notReleased}</span>
                    </span>
                  ))}
                </p>
              ) : null}
              {/* what the camera said after the shoot ("Efter inspelningen") */}
              {s.rushes ? <ShootNotes notes={s.rushes.notes ?? []} comments={s.rushes.comments ?? []} /> : null}
            </div>
            {v.can.edit ? (
              <div class="cluster sh-actions">
                {s.calendar.mode === 'manual' ? (
                  <button type="button" class="btn btn-sm" onClick={() => void invite(s)}>
                    {t.sendInvite}
                  </button>
                ) : s.calendar.status === 'failed' ? (
                  <button type="button" class="btn btn-sm" onClick={() => void invite(s)}>
                    {t.tryAgain}
                  </button>
                ) : null}
                <button type="button" class="btn btn-sm" onClick={() => setEdit({ shoot: s })}>
                  {t.change}
                </button>
                <button type="button" class="btn btn-sm btn-ghost" onClick={() => setCancelling(s)}>
                  {t.cancel}
                </button>
              </div>
            ) : null}
          </li>
        ))}
        {v.monday ? (
          <li class="sh-row is-monday">
            <DateBox date={v.monday.date} lang={lang} />
            <div class="sh-main">
              <p class="sh-when">{t.monday(v.monday.date)}</p>
              <p class="small muted">{t.mondaySub}</p>
            </div>
            {v.can.edit ? (
              <div class="cluster sh-actions">
                <button type="button" class="btn btn-sm" onClick={() => setEdit({ date: v.monday!.date })}>
                  {t.takeOver}
                </button>
              </div>
            ) : null}
          </li>
        ) : null}
      </ul>
      {!v.shoots.length && !v.monday ? <p class="muted">{v.can.edit ? t.none : t.noneOther}</p> : null}
      {v.cancelled.length ? (
        <details class="sh-cancelled">
          <summary class="small">{t.cancelledShoots(v.cancelled.length)}</summary>
          <ul class="list">
            {v.cancelled.map((s) => (
              <li class="small muted">
                {s.date} {s.start}–{s.end} · {s.location} · {t.cancelled}
                {s.cancelReason ? `: ${s.cancelReason}` : ''}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {edit && v.options ? <ShootDialog api={p.api} projectId={p.id} tab={v} options={v.options} shoot={edit.shoot} date={edit.date} onClose={() => setEdit(null)} onDone={done} /> : null}
      {cancelling ? <CancelDialog api={p.api} shoot={cancelling} onClose={() => setCancelling(null)} onDone={done} /> : null}
    </section>
  );
}

/**
 * Book a shoot without opening the project (owner 2026-10-31: from the project list). The same dialog as on the
 * Shoots tab, with the project's team, contacts and calendar loaded first.
 */
export function BookShoot(p: { api: Api; projectId: string; onClose: () => void; onDone: (r: { text: string }) => void }) {
  const [v, setV] = useState<Tab | null>(null);
  useEffect(() => {
    p.api.get<Tab>(`/api/projects/${p.projectId}/shoots`).then(setV, (e) => {
      toast({ text: errText(e) });
      p.onClose();
    });
  }, [p.projectId]);
  if (!v?.options) return null;
  return <ShootDialog api={p.api} projectId={p.projectId} tab={v} options={v.options} date={v.monday?.date} onClose={p.onClose} onDone={p.onDone} />;
}

interface Form {
  date: string;
  start: string;
  end: string;
  location: string;
  plan: string;
  contactId: string;
  guestContactIds: string[];
  crew: { personId: string; role: Role }[];
  coordinatorId: string;
  /** empty = the whole project */
  videoIds: string[];
  sendInvite: boolean;
  /** numbers typed or picked for people and contacts with none on their profile (saved there on save), by id */
  phones: Record<string, string>;
}

function initial(o: Options, s: ShootView | undefined, date: string | undefined): Form {
  if (s) {
    // a number the shoot has but the profile lacks is kept, so saving does not lose it
    const phones: Record<string, string> = {};
    const keep = (id: string, phone: string | null, profile: string | null | undefined) => {
      if (phone && !profile) phones[id] = phone;
    };
    for (const m of s.crew) keep(m.id, m.phone, o.people.find((x) => x.id === m.id)?.phone);
    if (s.coordinator) keep(s.coordinator.id, s.coordinator.phone, o.people.find((x) => x.id === s.coordinator!.id)?.phone);
    if (s.contact) keep(s.contact.id, s.contact.phone, o.contacts.find((x) => x.id === s.contact!.id)?.phone);
    return {
      date: s.date,
      start: s.start,
      end: s.end,
      location: s.location,
      plan: s.plan,
      contactId: s.contact?.id ?? '',
      guestContactIds: s.guests.map((g) => g.id),
      crew: s.crew.map((m) => ({ personId: m.id, role: m.role })),
      coordinatorId: s.coordinator?.id ?? '',
      videoIds: s.videos.map((x) => x.id),
      sendInvite: s.calendar.mode === 'hub',
      phones
    };
  }
  return {
    date: date ?? '',
    start: '08:00',
    end: '16:00',
    location: '',
    plan: '',
    contactId: o.defaults.contactId ?? '',
    guestContactIds: [],
    crew: o.defaults.crew,
    coordinatorId: o.defaults.coordinatorId ?? '',
    videoIds: o.defaults.videoIds,
    sendInvite: true,
    phones: {}
  };
}

function conflictText(c: Conflict, lang: Lang): string {
  const t = T[lang];
  switch (c.kind) {
    case 'free':
      return t.free;
    case 'away':
      return t.away[c.part];
    case 'shoot':
      return t.otherShoot(c.project, c.from, c.to);
    case 'busy':
      return t.busy(c.from, c.to);
    case 'unknown':
      return t.unknown;
  }
}

const ROLE_ORDER: Role[] = ['producer', 'cd', 'camera', 'editor', 'other'];

function ShootDialog(p: { api: Api; projectId: string; tab: Tab; options: Options; shoot?: ShootView; date?: string; onClose: () => void; onDone: (r: { text: string }) => void }) {
  const lang = useLang();
  const t = T[lang];
  const o = p.options;
  const [f, setF] = useState<Form>(() => initial(o, p.shoot, p.date));
  const [more, setMore] = useState(false);
  const [pickVideos, setPickVideos] = useState(() => f.videoIds.length > 0);
  const [busy, setBusy] = useState(false);
  const [bad, setBad] = useState<{ field: string; text: string } | null>(null);
  const [adding, setAdding] = useState<HTMLElement | null>(null);
  const [check, setCheck] = useState<{ key: string; people: { id: string; conflicts: Conflict[]; workday: boolean }[] } | null>(null);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const people = [...new Set([...f.crew.map((m) => m.personId), ...(f.coordinatorId ? [f.coordinatorId] : [])])];
  const key = `${f.date}|${f.start}|${f.end}|${people.join(',')}`;
  useEffect(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.date) || !people.length) return setCheck(null);
    let live = true;
    const h = setTimeout(() => {
      p.api.post<{ people: { id: string; conflicts: Conflict[]; workday: boolean }[] }>(`/api/projects/${p.projectId}/shoots/check`, { date: f.date, start: f.start, end: f.end, people, shootId: p.shoot?.id }).then(
        (r) => live && setCheck({ key, people: r.people }),
        () => live && setCheck(null)
      );
    }, 300);
    return () => {
      live = false;
      clearTimeout(h);
    };
  }, [key]);
  const conflictsOf = (id: string) => (check?.key === key ? check.people.find((x) => x.id === id)?.conflicts : undefined);
  const hasConflict = check?.key === key && check.people.some((x) => x.conflicts.some((c) => c.kind !== 'free' && c.kind !== 'unknown'));
  const notWorkday = check?.key === key && check.people[0] && !check.people[0].workday;
  const person = (id: string) => o.people.find((x) => x.id === id);
  const name = (id: string) => person(id)?.name ?? '–';
  const first = (n: string) => n.split(/\s+/)[0] ?? n;
  const addable = o.people.filter((x) => !f.crew.some((m) => m.personId === x.id));
  // phones come from the profile or contact card; a missing one is typed or picked once and saved there
  const profilePhone = (id: string) => person(id)?.phone ?? o.contacts.find((c) => c.id === id)?.phone ?? null;
  const phoneOf = (id: string) => profilePhone(id) ?? (f.phones[id]?.trim() || null);
  const setPhone = (id: string, v: string) => setF((x) => ({ ...x, phones: { ...x.phones, [id]: v } }));
  const contact = o.contacts.find((c) => c.id === f.contactId);
  const missing = [
    ...people.map((id) => ({ id, name: name(id) })),
    ...(contact ? [{ id: contact.id, name: contact.name }] : [])
  ].filter((x) => !profilePhone(x.id));
  const save = async () => {
    setBusy(true);
    setBad(null);
    const typed = (id: string | null) => (id && !profilePhone(id) ? f.phones[id]?.trim() || null : null);
    const body = {
      date: f.date,
      start: f.start,
      end: f.end,
      location: f.location,
      plan: f.plan,
      contactId: f.contactId || null,
      contactPhone: typed(f.contactId || null),
      guestContactIds: f.guestContactIds,
      coordinatorId: f.coordinatorId || null,
      coordinatorPhone: typed(f.coordinatorId || null),
      videoIds: pickVideos ? f.videoIds : [],
      sendInvite: f.sendInvite,
      crew: f.crew.map((m) => ({ personId: m.personId, role: m.role, phone: typed(m.personId) }))
    };
    try {
      const r = p.shoot ? await p.api.put<{ text: string }>(`/api/shoots/${p.shoot.id}`, body) : await p.api.post<{ text: string }>(`/api/projects/${p.projectId}/shoots`, body);
      p.onClose();
      p.onDone(r);
    } catch (e) {
      const field = e instanceof ApiError ? (e.details as { field?: string } | undefined)?.field : undefined;
      if (field) {
        setBad({ field, text: errText(e) });
        if (['coordinatorId', 'videoIds', 'guestContactIds', 'plan'].includes(field)) setMore(true);
      } else toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  const err = (field: string) => (bad && (bad.field === field || bad.field.startsWith(`${field}.`)) ? <p class="field-error">{bad.text}</p> : null);
  const inv = (field: string) => (bad?.field === field ? { 'aria-invalid': 'true' as const } : {});
  const askInvite = !p.shoot || p.shoot.calendar.mode === 'manual';
  const label = p.shoot ? (f.sendInvite && p.shoot.calendar.mode === 'hub' ? t.saveChange : f.sendInvite ? t.saveSend : t.save) : !f.sendInvite ? t.register : hasConflict ? t.bookAnyway : t.bookSend;
  const videosSummary = pickVideos ? t.videos(f.videoIds.length) : t.wholeProject;
  const summary = [videosSummary, f.coordinatorId ? t.coordinatorShort(first(name(f.coordinatorId))) : null, f.guestContactIds.length ? t.guestsShort(f.guestContactIds.length) : null, f.plan.trim() ? t.planShort : t.noPlan].filter(Boolean).join(' · ');
  return (
    <Dialog
      title={p.shoot ? t.dlgChange : t.dlgBook}
      onClose={p.onClose}
      foot={
        <>
          {askInvite ? (
            <label class="check sh-foot-invite">
              <input type="checkbox" checked={f.sendInvite} onChange={(e) => set('sendInvite', (e.target as HTMLInputElement).checked)} />
              <span>{t.sendFrom(p.tab.calendar.organizer)}</span>
            </label>
          ) : null}
          <button type="button" class="btn" onClick={p.onClose}>
            {t.close}
          </button>
          <button type="button" class="btn btn-primary" disabled={busy} onClick={() => void save()}>
            {busy ? t.saving : label}
          </button>
        </>
      }
    >
      <div class="stack sh-form">
        <div class="sh-row1">
          <label class="field">
            <span class="label">{t.date}</span>
            <input class="input input--wide" type="date" value={f.date} onInput={(e) => set('date', val(e))} {...inv('date')} />
            {err('date')}
          </label>
          <label class="field">
            <span class="label">{t.start}</span>
            <input class="input input--wide" type="time" step={900} value={f.start} onInput={(e) => set('start', val(e))} {...inv('start')} />
            {err('start')}
          </label>
          <label class="field">
            <span class="label">{t.end}</span>
            <input class="input input--wide" type="time" step={900} value={f.end} onInput={(e) => set('end', val(e))} {...inv('end')} />
            {err('end')}
          </label>
        </div>
        <div class="sh-row2">
          <label class="field">
            <span class="label">{t.location}</span>
            <input class="input input--wide" value={f.location} placeholder={t.locationHint} onInput={(e) => set('location', val(e))} {...inv('location')} />
            {err('location')}
          </label>
          <label class="field">
            <span class="label">{t.onSet}</span>
            <select class="input input--wide" value={f.contactId} onChange={(e) => setF((x) => ({ ...x, contactId: val(e), guestContactIds: x.guestContactIds.filter((g) => g !== val(e)) }))}>
              <option value="">{t.noContact}</option>
              {o.contacts.map((c) => (
                <option value={c.id}>{c.name}</option>
              ))}
            </select>
            {contact && phoneOf(contact.id) ? <span class="hint">{phoneOf(contact.id)}</span> : null}
            {err('contactId')}
          </label>
        </div>
        <fieldset class="fieldset">
          <legend class="label sh-legend">
            <span>{t.crew}</span>
            <span class="hint">{t.fromTeam}</span>
          </legend>
          <ul class="sh-chips">
            {f.crew.map((m, i) => {
              const ph = phoneOf(m.personId);
              return (
                <li class="sh-chip">
                  <Avatar name={name(m.personId)} size="sm" decorative />
                  <span class="sh-chip-main">
                    <span class="sh-chip-name">{name(m.personId)}</span>
                    <select class="sh-chip-role" aria-label={`${t.roleLabel} ${name(m.personId)}`} value={m.role} onChange={(e) => set('crew', f.crew.map((x, j) => (j === i ? { ...x, role: val(e) as Role } : x)))}>
                      {ROLE_ORDER.map((r) => (
                        <option value={r}>{t.role[r]}</option>
                      ))}
                    </select>
                    <span class={`sh-chip-phone${ph ? '' : ' is-missing'}`}>{ph ?? t.noPhone}</span>
                  </span>
                  <Status cs={conflictsOf(m.personId)} lang={lang} short />
                  <button type="button" class="sh-chip-x" aria-label={`${t.remove} ${name(m.personId)}`} onClick={() => set('crew', f.crew.filter((_, j) => j !== i))}>
                    ✕
                  </button>
                </li>
              );
            })}
            {addable.length ? (
              <li>
                <button
                  type="button"
                  class={`sh-chip-add${adding ? ' is-open' : ''}`}
                  aria-expanded={Boolean(adding)}
                  onClick={(e) => setAdding(adding ? null : (e.currentTarget as HTMLElement))}
                >
                  <AddBall size="sm" />
                  {t.addPerson}
                </button>
                {adding ? (
                  <PersonPicker
                    label={t.addPerson}
                    people={addable}
                    current={[]}
                    multi={false}
                    at={adding}
                    onClose={() => setAdding(null)}
                    onPick={async ([id]) => {
                      const x = o.people.find((y) => y.id === id);
                      if (x) set('crew', [...f.crew, { personId: x.id, role: x.camera ? 'camera' : 'other' }]);
                    }}
                  />
                ) : null}
              </li>
            ) : null}
          </ul>
          {err('crew')}
        </fieldset>
        {missing.length ? (
          <div class={`sh-missing${missing.every((x) => f.phones[x.id]?.trim()) ? ' is-done' : ''}`} role="group" aria-label={t.missingTitle}>
            <p class="small">
              <strong>{missing.every((x) => f.phones[x.id]?.trim()) ? t.newPhones : t.missingTitle}</strong> <span class="muted">{t.missingSub}</span>
            </p>
            {missing.map((x) => {
              const sug = o.suggestions[x.id];
              const typed = f.phones[x.id] ?? '';
              return (
                <div class="sh-missing-row">
                  <span class="sh-missing-name">{x.name}</span>
                  {sug && typed !== sug.phone ? (
                    <button type="button" class="btn btn-sm" onClick={() => setPhone(x.id, sug.phone)}>
                      {t.useSuggestion(sug.phone)}
                    </button>
                  ) : null}
                  <input class="input sh-missing-in" type="tel" placeholder={t.phonePlaceholder} aria-label={`${t.phone} ${x.name}`} value={typed} onInput={(e) => setPhone(x.id, val(e))} />
                  {sug && typed !== sug.phone ? <span class="hint">{t.suggestionFrom(sug.from, sug.date)}</span> : null}
                  {err(x.id === f.contactId ? 'contactPhone' : x.id === f.coordinatorId ? 'coordinatorPhone' : `crew.${x.id}`)}
                </div>
              );
            })}
          </div>
        ) : null}
        <button type="button" class="sh-more" aria-expanded={more ? 'true' : 'false'} onClick={() => setMore(!more)}>
          <span class={`sh-more-chev${more ? ' is-open' : ''}`} aria-hidden="true">
            ›
          </span>
          <strong>{t.more}</strong>
          <span class="muted sh-more-sum">{summary}</span>
        </button>
        {more ? (
          <div class="stack sh-more-body">
            <div class="sh-row2">
              <label class="field">
                <span class="label">{t.coordinator}</span>
                <select class="input input--wide" value={f.coordinatorId} onChange={(e) => set('coordinatorId', val(e))}>
                  <option value="">–</option>
                  {o.people
                    .filter((x) => !x.freelancer)
                    .map((x) => (
                      <option value={x.id}>{x.name}</option>
                    ))}
                </select>
                {f.coordinatorId && !f.crew.some((m) => m.personId === f.coordinatorId) ? <Status cs={conflictsOf(f.coordinatorId)} lang={lang} /> : null}
                {err('coordinatorId')}
              </label>
              {o.videos.length ? (
                <label class="field">
                  <span class="label">{t.filmed}</span>
                  <select class="input input--wide" value={pickVideos ? 'some' : 'all'} onChange={(e) => setPickVideos(val(e) === 'some')}>
                    <option value="all">{t.wholeProjectN(o.videos.length)}</option>
                    <option value="some">{t.someVideos}</option>
                  </select>
                </label>
              ) : null}
            </div>
            {pickVideos ? (
              <div class="cluster">
                {o.videos.map((x) => (
                  <label class="check">
                    <input type="checkbox" checked={f.videoIds.includes(x.id)} onChange={(e) => set('videoIds', (e.target as HTMLInputElement).checked ? [...f.videoIds, x.id] : f.videoIds.filter((y) => y !== x.id))} />
                    <span>{x.name}</span>
                  </label>
                ))}
                {err('videoIds')}
              </div>
            ) : null}
            {o.contacts.filter((c) => c.id !== f.contactId).length ? (
              <fieldset class="fieldset">
                <legend class="label">{t.alsoInvite}</legend>
                <div class="cluster">
                  {o.contacts
                    .filter((c) => c.id !== f.contactId)
                    .map((c) => (
                      <label class="check">
                        <input type="checkbox" checked={f.guestContactIds.includes(c.id)} onChange={(e) => set('guestContactIds', (e.target as HTMLInputElement).checked ? [...f.guestContactIds, c.id] : f.guestContactIds.filter((x) => x !== c.id))} />
                        <span>{c.name}</span>
                      </label>
                    ))}
                </div>
              </fieldset>
            ) : null}
            <label class="field">
              <span class="label">{t.plan}</span>
              <textarea class="input input--wide sh-plan-in" rows={3} placeholder={t.planHint} value={f.plan} onInput={(e) => set('plan', val(e))} />
              {err('plan')}
            </label>
          </div>
        ) : null}
        {notWorkday ? <Callout tone="warn" title={t.weekend} /> : null}
        {hasConflict ? <Callout tone="warn" title={t.conflictWarn} /> : null}
      </div>
    </Dialog>
  );
}

function Status(p: { cs: Conflict[] | undefined; lang: Lang; short?: boolean }) {
  if (!p.cs) return <span class="small muted sh-st">…</span>;
  const real = p.cs.filter((c) => c.kind !== 'free');
  if (!real.length)
    return (
      <span class="small sh-st is-ok" title={conflictText({ kind: 'free' }, p.lang)}>
        ● {conflictText({ kind: 'free' }, p.lang)}
      </span>
    );
  const text = real.map((c) => conflictText(c, p.lang)).join(' · ');
  const tone = real.some((c) => c.kind === 'away' || c.kind === 'shoot') ? 'is-crit' : real.every((c) => c.kind === 'unknown') ? '' : 'is-warn';
  // the chip only has room for the times; the whole text is in the tooltip
  const brief = p.short ? real.map((c) => (c.kind === 'busy' || c.kind === 'shoot' ? `${c.from}–${c.to}` : conflictText(c, p.lang))).join(', ') : text;
  return (
    <span class={`small sh-st ${tone}`} title={text}>
      ● {brief}
    </span>
  );
}

function CancelDialog(p: { api: Api; shoot: ShootView; onClose: () => void; onDone: (r: { text: string }) => void }) {
  const t = T[useLang()];
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      title={t.cancelTitle}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.keep}
          </button>
          <button
            type="button"
            class="btn btn-danger"
            disabled={busy || !reason.trim()}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await p.api.post<{ text: string }>(`/api/shoots/${p.shoot.id}/cancel`, { reason });
                p.onClose();
                p.onDone(r);
              } catch (e) {
                toast({ text: errText(e) });
              } finally {
                setBusy(false);
              }
            }}
          >
            {t.doCancel}
          </button>
        </>
      }
    >
      <p>
        {p.shoot.date} {p.shoot.start}–{p.shoot.end} · {p.shoot.location}
      </p>
      <p class="hint">{p.shoot.calendar.mode === 'hub' && p.shoot.calendar.status === 'sent' ? t.cancelBody : t.cancelManual}</p>
      <label class="field">
        <span class="label">{t.reason}</span>
        <input class="input input--wide" value={reason} onInput={(e) => setReason(val(e))} />
      </label>
    </Dialog>
  );
}
